import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import {
  FaEnvelope,
  FaPhone,
  FaLinkedin,
  FaGithub,
  FaPaperPlane,
  FaCheckCircle,
  FaUser,
  FaCommentDots,
  FaHeadset
} from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import './Contact.css';

const WEB3FORMS_ACCESS_KEY = "810b3780-ac40-49c0-aaf0-a7ebbeeff774";

// Files live inside the /public folder
const BG_VIDEO = "/videos/contact-bg.mp4";
const BG_IMAGE = "/images/contact-bg.jpg";

const ease = [0.22, 1, 0.36, 1];

const rise = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease } }
};

const stagger = (delay = 0, gap = 0.08) => ({
  hidden: {},
  show: { transition: { delayChildren: delay, staggerChildren: gap } }
});

const quickLinks = [
  {
    icon: <FaEnvelope />,
    title: "Gmail",
    gradient: "linear-gradient(135deg, #EA4335, #FF6B6B)",
    value: "amitgingale@gmail.com",
    href: "https://mail.google.com/mail/?view=cm&fs=1&to=amitgingale@gmail.com"
  },
  {
    icon: <FaLinkedin />,
    title: "LinkedIn",
    gradient: "linear-gradient(135deg, #0077B5, #00A0DC)",
    value: "amitgingale07",
    href: "https://linkedin.com/in/amitgingale07"
  },
  {
    icon: <FaGithub />,
    title: "GitHub",
    gradient: "linear-gradient(135deg, #3b3b4f, #8b6fd1)",
    value: "AmitIngAI",
    href: "https://github.com/AmitIngAI"
  }
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: ''
  });
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const videoRef = useRef(null);

  // Some browsers ignore the muted attribute on first render, so force autoplay
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg('');

    const payload = new FormData(e.target);
    payload.append("access_key", WEB3FORMS_ACCESS_KEY);
    payload.append("from_name", "Portfolio Contact Form");
    if (!formData.subject) {
      payload.set("subject", "New Contact from Portfolio");
    }

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: payload
      });
      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => setIsSubmitted(false), 4000);
      } else {
        setErrorMsg(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setErrorMsg('Network error. Please check your internet and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Soft light that follows the mouse inside the panel
  const onPanelMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  const field = (name) =>
    `form-group ${focusedField === name || formData[name] ? 'focused' : ''}`;

  const bind = (name) => ({
    name,
    id: name,
    value: formData[name],
    onChange: handleChange,
    onFocus: () => setFocusedField(name),
    onBlur: () => setFocusedField(null)
  });

  return (
    <MotionConfig reducedMotion="user">
      <div className="page contact-page">
        {/* Background: video is limited to one screen height, never stretched */}
        <div className="contact-bg" aria-hidden="true">
          <video
            ref={videoRef}
            className="contact-bg-video"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={BG_IMAGE}
          >
            <source src={BG_VIDEO} type="video/mp4" />
          </video>
          <img className="contact-bg-image" src={BG_IMAGE} alt="" />
          <div className="contact-bg-overlay"></div>
        </div>

        <div className="contact-center">
          <motion.header
            className="contact-heading"
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
          >
            <motion.div
              className="hero-icon"
              animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.08, 1] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <FaHeadset />
            </motion.div>

            <h1 className="contact-title">
              <span className="title-gradient">Let's Connect</span>
              <motion.span
                className="title-sparkle"
                animate={{ rotate: [0, 20, 0], scale: [1, 1.15, 1] }}
                transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 2 }}
              >
                <HiSparkles />
              </motion.span>
            </h1>

            <motion.span
              className="title-underline"
              initial={{ width: 0 }}
              animate={{ width: 64 }}
              transition={{ duration: 0.8, delay: 0.4, ease }}
            />

            <p>Have a project in mind or want to collaborate? I'd love to hear from you!</p>
          </motion.header>

          <motion.section
            className="contact-panel"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15, ease }}
            onMouseMove={onPanelMove}
          >

            {/* Quick connect */}
            <motion.nav
              className="quick-connect"
              aria-label="Quick connect"
              variants={stagger(0.4, 0.07)}
              initial="hidden"
              animate="show"
            >
              {quickLinks.map((l) => (
                <motion.a
                  key={l.title}
                  className="quick-link"
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={rise}
                >
                  <span className="quick-icon" style={{ background: l.gradient }}>{l.icon}</span>
                  <span className="quick-text">
                    <strong>{l.title}</strong>
                    <small>{l.value}</small>
                  </span>
                </motion.a>
              ))}
            </motion.nav>

            <motion.div
              className="panel-divider"
              initial={{ opacity: 0, scaleX: 0.6 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.6, ease }}
            >
              <span>or write to me here</span>
            </motion.div>

            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div
                  key="done"
                  className="success-message"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, ease }}
                >
                  <div className="success-icon"><FaCheckCircle /></div>
                  <h3>Message sent</h3>
                  <p>Thanks for reaching out. I'll reply within 24 hours.</p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  className="contact-form"
                  variants={stagger(0.7, 0.06)}
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0, transition: { duration: 0.25 } }}
                >
                  {/* Honeypot spam protection (hidden) */}
                  <input
                    type="checkbox"
                    name="botcheck"
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <motion.div className="form-row" variants={rise}>
                    <div className={field('name')}>
                      <div className="input-icon"><FaUser /></div>
                      <input type="text" {...bind('name')} required />
                      <label htmlFor="name">Your name *</label>
                      <div className="input-border"></div>
                    </div>
                    <div className={field('email')}>
                      <div className="input-icon"><FaEnvelope /></div>
                      <input type="email" {...bind('email')} required />
                      <label htmlFor="email">Your email *</label>
                      <div className="input-border"></div>
                    </div>
                  </motion.div>

                  <motion.div className="form-row" variants={rise}>
                    <div className={field('phone')}>
                      <div className="input-icon"><FaPhone /></div>
                      <input type="tel" {...bind('phone')} />
                      <label htmlFor="phone">Phone (optional)</label>
                      <div className="input-border"></div>
                    </div>
                    <div className={field('subject')}>
                      <div className="input-icon"><FaCommentDots /></div>
                      <input type="text" {...bind('subject')} required />
                      <label htmlFor="subject">Subject *</label>
                      <div className="input-border"></div>
                    </div>
                  </motion.div>

                  <motion.div className={`${field('message')} textarea-group`} variants={rise}>
                    <textarea rows="4" maxLength={500} {...bind('message')} required></textarea>
                    <label htmlFor="message">Your message *</label>
                    <div className="input-border"></div>
                    <span className="char-count">{formData.message.length} / 500</span>
                  </motion.div>

                  {errorMsg && <p className="form-error">{errorMsg}</p>}

                  <motion.div variants={rise}>
                    <motion.button
                      type="submit"
                      className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane /> Send message
                        </>
                      )}
                    </motion.button>
                  </motion.div>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.section>
        </div>
      </div>
    </MotionConfig>
  );
};

export default Contact;